import React from "react";
import { getCommandParams, getFilters, getFormDesign, getObjectState, getRecData } from "../../../redux/selectors";
import { useDispatch, useSelector } from "react-redux";
import {
  changeToBuffer,
  setChange,
  setFilter,
  setGridChange,
  setObjectState,
} from "../../../redux/features/ui/uiSlice";
import { calculate } from "../../../services/calculate";
import { TiDeleteOutline } from "react-icons/ti";
import { BsCalendar3 } from "react-icons/bs";
import { InputGroup, FloatingLabel, Form, Button } from "react-bootstrap";

const S1DateTime = ({ element, tabID, screenState, currentLine, grid }) => {
  const table = element.name.split(".")[0];
  const key = element.name.split(".")[1];

  const commandParams = useSelector((state) => getCommandParams(state, tabID));

  const dispatch = useDispatch();
  const objectState = useSelector((state) => getObjectState(state, tabID));
  const filters = useSelector((state) => getFilters(state, tabID));
  const formDesign = useSelector((state) => getFormDesign(state, tabID));
  const model = screenState == "form" && formDesign && formDesign.model[table].fields.filter((el) => el.name == key)[0];
  const data = useSelector((state) => getRecData(state, tabID));
  const tableIndex = formDesign?.model?.[table]?.relationship == "OneToOne" ? 0 : currentLine?.index ?? 0;

  const dateRef = React.useRef(null);
  const contRef = React.useRef(null);

  const [clear, showClear] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [value, setValue] = React.useState("");
  const [viewValue, setViewValue] = React.useState("");

  const handleCalculate = async (dtValue) => {
    if (grid) {
      dispatch(setGridChange({ tabID, name: grid.model, key, value: dtValue }));
    } else {
      dispatch(setChange({ tabID, table, key, value: dtValue }));
      model?.updates && (await calculate(tabID));
    }
  };

  const handleClear = () => {
    if (screenState == "dialog") {
      dispatch(setFilter({ tabID, table, key, value: "" }));
    } else {
      if (objectState == "view") {
        dispatch(changeToBuffer({ tabID }));
        dispatch(setObjectState({ tabID, value: "edit" }));
      }
      handleCalculate("");
    }
  };

  React.useEffect(() => {
    if (isFocused) {
      dateRef.current.showPicker();
      dateRef.current.click();
      dateRef.current.focus();
    }
  }, [isFocused]);

  React.useEffect(() => {
    if (screenState == "dialog") {
      setValue(element.value);
      setViewValue(element.value);
      dispatch(setFilter({ tabID, table, key, value: element.value }));
    }
  }, [screenState]);

  React.useEffect(() => {
    if (screenState == "form" && !grid) {
      if (data?.[table]?.[0]?.[key] && data?.[table]?.[0]?.[key] != "") {
        setValue(dateTimeUIConverter(data?.[table]?.[0]?.[key]));
        setViewValue(displayConverter(data?.[table]?.[0]?.[key]));
      } else {
        setValue("");
        setViewValue("");
      }
    }
  }, [data?.[table]?.[0]?.[key]]);

  const handleChange = (value) => {
    if (screenState == "dialog") {
      dispatch(setFilter({ tabID, table, key, value: dateTimeConverter(value) }));
    } else if (screenState == "form") {
      handleCalculate(dateTimeConverter(value));
    }
  };

  React.useEffect(() => {
    if (grid) {
      if (currentLine?.line?.[key] && currentLine?.line?.[key] != "") {
        setValue(dateTimeConverter(currentLine?.line?.[key]));
        setViewValue(displayConverter(currentLine?.line?.[key]));
      } else {
        setValue("");
        setViewValue("");
      }
    }
  }, [currentLine?.line?.[key]]);

  return (
    <div
      className="s1calendardate w-100"
      ref={contRef}
      style={{
        flex: `1 1 ${element.flex * 100}%`,
        overflow: "hidden",
        visibility: element.visible ? "visible" : "hidden",
      }}
    >
      <InputGroup className={`crm-floating-with-icon${element.required && value == "" ? " input-required" : ""}`}>
        <FloatingLabel
          controlId={element.index}
          label={element.caption}
          style={{ whiteSpace: "nowrap" }}
          className="w-100"
        >
          <Form.Control
            onClick={() => objectState != "view" && setIsFocused(true)}
            className={`d-flex w-100 ps-1 border-0${value != "" ? " date-input-has-value" : ""}${
              element.readOnly || (screenState == "form" && commandParams.readonly) ? " disabled" : ""
            }`}
            disabled={
              element.readOnly ||
              (screenState == "form" && commandParams.readonly) ||
              (screenState == "form" && objectState == "view")
            }
            readOnly={true}
            value={viewValue}
            name={element.name}
            placeholder={element.caption}
          />
        </FloatingLabel>
        {objectState != "view" && value != "" && !element.readOnly && (
          <div className="d-flex align-items-center justify-content-center me-1" onClick={handleClear}>
            <TiDeleteOutline size="1.75rem" />
          </div>
        )}
        {!(element.readOnly || (screenState == "form" && commandParams.readonly)) && (
          <div
            className="d-flex align-items-center justify-content-center"
            role="button"
            onClick={() => objectState != "view" && setIsFocused(true)}
          >
            <BsCalendar3
              className={
                element.readOnly ||
                (screenState == "form" && commandParams.readonly) ||
                (screenState == "form" && objectState == "view")
                  ? "disabled"
                  : ""
              }
              color="var(--bs-primary)"
              size="20px"
            />
          </div>
        )}
      </InputGroup>
      <input
        type="datetime-local"
        ref={dateRef}
        value={value}
        className="text-truncate"
        onBlur={() => setIsFocused(false)}
        onChange={(e) => !element.readOnly && handleChange(e.target.value)}
        style={{
          pointerEvents: "none",
          position: "fixed",
          top: contRef?.current?.getBoundingClientRect()?.bottom,
          maxWidth: 0,
          maxHeight: 0,
          backgroundColor: "transparent",
          border: 0,
          color: "transparent",
        }}
      />
    </div>
  );
};

export default S1DateTime;

const dateTimeConverter = (value) => {
  if (value != "") {
    var d = new Date(value);
    return `${[d.getFullYear(), (d.getMonth() + 1).padLeft(), d.getDate().padLeft()].join("-")} ${[
      d.getHours().padLeft(),
      d.getMinutes().padLeft(),
      d.getMilliseconds().padLeft(),
    ].join(":")}`;
  } else {
    return value;
  }
};

const dateTimeUIConverter = (value) => {
  if (value != "") {
    var d = new Date(value);
    return `${[d.getFullYear(), (d.getMonth() + 1).padLeft(), d.getDate().padLeft()].join("-")}T${[
      d.getHours().padLeft(),
      d.getMinutes().padLeft(),
    ].join(":")}`;
  } else {
    return value;
  }
};

const displayConverter = (value) => {
  if (value != "") {
    var d = new Date(value);
    return `${[d.getDate().padLeft(), (d.getMonth() + 1).padLeft(), d.getFullYear()].join("/")} ${[
      d.getHours().padLeft(),
      d.getMinutes().padLeft(),
    ].join(":")}`;
  } else {
    return value;
  }
};
